import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  type ChipColorTypes,
  Icon,
  type IconKind,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import type { ImmediateAction } from '../../data-layer/insightDashboardTypes';

import './ActionsPanel.css';

interface ActionsPanelProps {
  actions: ImmediateAction[];
  onToggleAction: (id: number) => void;
  className?: string;
}

const defaultProps = {
  className: undefined,
};

interface CategoryInfo {
  name: string;
  iconKind: string;
  accentVar: string;
  items: ImmediateAction[];
  completedCount: number;
}

const CATEGORY_META: Record<
  string,
  { icon: string; accentVar: string } | undefined
> = {
  'Account Security': {
    icon: 'ShieldOutline',
    accentVar: '--id-severity-critical',
  },
  'Financial Remediation': {
    icon: 'CurrencyDollarOutline',
    accentVar: '--id-severity-high',
  },
  'System Integrity': {
    icon: 'ServerOutline',
    accentVar: '--id-severity-medium',
  },
  Investigation: { icon: 'SearchOutline', accentVar: '--id-severity-low' },
};

const CATEGORY_ORDER = [
  'Account Security',
  'Financial Remediation',
  'System Integrity',
  'Investigation',
];

const PRIORITY_CHIP_MAP: Record<
  string,
  { label: string; color: ChipColorTypes } | undefined
> = {
  critical: { label: 'Critical', color: 'pink' },
  high: { label: 'High', color: 'orange' },
  medium: { label: 'Medium', color: 'warning' },
};

const PRIORITY_ORDER: Record<string, number | undefined> = {
  critical: 0,
  high: 1,
  medium: 2,
};

function getHighestPriority(items: ImmediateAction[]): string {
  return items.reduce(
    (highest, item) =>
      (PRIORITY_ORDER[item.priority] ?? 2) < (PRIORITY_ORDER[highest] ?? 2)
        ? item.priority
        : highest,
    'medium'
  );
}

/**
 * Renders the immediate actions panel with categorised checklist items and a progress indicator.
 * @param props - Component props.
 * @param props.actions - List of immediate actions to display.
 * @param props.onToggleAction - Callback invoked when an action is toggled.
 * @param props.className - Optional additional CSS class.
 * @returns The rendered actions panel.
 */
function ActionsPanel({
  actions,
  onToggleAction,
  className,
}: ActionsPanelProps) {
  const [openCategory, setOpenCategory] = useState<string | null>(null);

  const totalCompleted = useMemo(
    () => actions.filter((a) => a.completed).length,
    [actions]
  );

  const progressPercent =
    actions.length > 0
      ? Math.round((totalCompleted / actions.length) * 100)
      : 0;

  const categories: CategoryInfo[] = useMemo(() => {
    const grouped: Record<string, ImmediateAction[] | undefined> = {};

    actions.forEach((action) => {
      if (!grouped[action.category]) {
        grouped[action.category] = [];
      }

      grouped[action.category]?.push(action);
    });

    const ordered = CATEGORY_ORDER.filter((cat) => Boolean(grouped[cat]));
    const remaining = Object.keys(grouped).filter(
      (cat) => !CATEGORY_ORDER.includes(cat)
    );

    return [...ordered, ...remaining].map((name) => {
      const meta = CATEGORY_META[name];
      const items = grouped[name] ?? [];

      return {
        name,
        iconKind: meta ? meta.icon : 'FolderOutline',
        accentVar: meta ? meta.accentVar : '--id-severity-medium',
        items,
        completedCount: items.filter((a) => a.completed).length,
      };
    });
  }, [actions]);

  const activeCategory = categories.find((c) => c.name === openCategory);

  return (
    <Box className={cx('Sod-ActionsPanel', className)}>
      {/* Header */}
      <Box className='Sod-ActionsPanel-header'>
        <Box className='Sod-ActionsPanel-header-left'>
          <Typography kind='h3' className='Sod-ActionsPanel-header-title'>
            {'Immediate Actions Required' as string}
          </Typography>
        </Box>
        <Chip
          kind='subtleFilled'
          size='xSmall'
          color='success'
          label={`${totalCompleted}/${actions.length}`}
        />
      </Box>

      {/* Progress bar */}
      <Box className='Sod-ActionsPanel-progress'>
        <Box className='Sod-ActionsPanel-progress-track'>
          <Box
            className='Sod-ActionsPanel-progress-fill'
            style={{ width: `${progressPercent}%` }}
          />
        </Box>
        <Typography kind='label' className='Sod-ActionsPanel-progress-label'>
          {progressPercent}
          {' % complete'}
        </Typography>
      </Box>

      {/* Category rows */}
      <Box className='Sod-ActionsPanel-categories'>
        {categories.map((cat) => {
          const priority = getHighestPriority(cat.items);
          const catChip = PRIORITY_CHIP_MAP[priority];

          return (
            <button
              key={cat.name}
              type='button'
              className='Sod-ActionsPanel-categoryRow'
              style={{ borderLeftColor: `var(${cat.accentVar})` }}
              onClick={() => setOpenCategory(cat.name)}>
              <Box className='Sod-ActionsPanel-categoryRow-left'>
                <Icon
                  kind={cat.iconKind as unknown as IconKind}
                  size='small'
                  color={`var(${cat.accentVar})`}
                />
                <Typography
                  kind='body1'
                  className='Sod-ActionsPanel-categoryRow-name'>
                  {cat.name}
                </Typography>
              </Box>
              <Box className='Sod-ActionsPanel-categoryRow-right'>
                <Typography
                  kind='label'
                  className='Sod-ActionsPanel-categoryRow-count'>
                  {cat.completedCount}
                  {' / '}
                  {cat.items.length}
                </Typography>
                {catChip ? (
                  <Chip
                    kind='outlined'
                    size='xSmall'
                    color={catChip.color}
                    label={catChip.label}
                  />
                ) : null}
                <Icon
                  kind={'ChevronRightOutline' as unknown as IconKind}
                  size='small'
                />
              </Box>
            </button>
          );
        })}
      </Box>

      <ModalIsland
        isOpen={Boolean(openCategory && activeCategory)}
        onClose={() => setOpenCategory(null)}
        headerIcon={
          <Icon
            kind={
              (activeCategory?.iconKind ??
                'FolderOutline') as unknown as IconKind
            }
            size='medium'
            color={
              activeCategory ? `var(${activeCategory.accentVar})` : undefined
            }
          />
        }
        title={activeCategory ? activeCategory.name : ''}
        contentBody={
          <Box className='Sod-ActionsPanel-modal-body'>
            {activeCategory?.items.map((item) => {
              const itemChip = PRIORITY_CHIP_MAP[item.priority];

              return (
                <Box
                  key={item.id}
                  className={cx('Sod-ActionsPanel-checkItem', {
                    'Sod-ActionsPanel-checkItem--completed': item.completed,
                  })}>
                  <div
                    className='Sod-ActionsPanel-checkItem-checkbox'
                    role='button'
                    tabIndex={0}
                    onClick={() => onToggleAction(item.id)}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onToggleAction(item.id);
                      }
                    }}>
                    {item.completed ? (
                      <Icon
                        kind={'CheckCircleOutline' as unknown as IconKind}
                        size='small'
                        color='var(--color-foreground-success-bold)'
                      />
                    ) : (
                      <div className='Sod-ActionsPanel-checkItem-unchecked' />
                    )}
                  </div>
                  <Typography
                    kind='body1'
                    className={cx('Sod-ActionsPanel-checkItem-text', {
                      'Sod-ActionsPanel-checkItem-text--completed':
                        item.completed,
                    })}>
                    {item.action}
                  </Typography>
                  {itemChip ? (
                    <Chip
                      kind='outlined'
                      size='xSmall'
                      color={itemChip.color}
                      label={itemChip.label}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Box>
        }
      />
    </Box>
  );
}

ActionsPanel.defaultProps = defaultProps;

export default ActionsPanel;
